import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Instagram, Facebook, Phone, Mail } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/data/content";
import { useSettings } from "@/context/SettingsContext";
import { api, resolveImg } from "@/lib/api";

export const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const settings = useSettings();
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then((res) => setServices(res.data)).catch(() => {});
  }, []);

  const goTo = (id) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer data-testid="footer" className="bg-black border-t border-white/10 pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-1">
            <img src={resolveImg(settings.logo_url)} alt="Klusbedrijf Pruijn" style={{ height: `${settings.footer_logo_height}px` }} className="w-auto object-contain -ml-2" />
            <p className="text-white/50 text-sm leading-relaxed mt-4 max-w-xs">
              Complete renovaties, verbouwingen en hoogwaardige afwerkingen met
              oog voor detail.
            </p>
            <div className="flex gap-3 mt-6">
              <a href={settings.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="w-10 h-10 border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-[#FF1A1A] transition-colors">
                <Instagram size={18} />
              </a>
              <a href={settings.facebook} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" className="w-10 h-10 border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-[#FF1A1A] transition-colors">
                <Facebook size={18} />
              </a>
              <a href={settings.tiktok} target="_blank" rel="noopener noreferrer" data-testid="footer-tiktok" aria-label="TikTok" className="w-10 h-10 border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-[#FF1A1A] transition-colors">
                <svg viewBox="0 0 24 24" className="w-[18px] h-[18px] fill-current"><path d="M16.6 5.82a4.28 4.28 0 0 1-1.06-2.82h-3.3v12.93a2.59 2.59 0 0 1-2.59 2.5 2.59 2.59 0 1 1 .73-5.07v-3.36a5.93 5.93 0 0 0-.73-.05A5.95 5.95 0 1 0 15.6 15.9V9.4a7.57 7.57 0 0 0 4.4 1.4V7.5a4.28 4.28 0 0 1-3.4-1.68z"/></svg>
              </a>
              <a href={settings.google} target="_blank" rel="noopener noreferrer" data-testid="footer-google" aria-label="Google" className="w-10 h-10 border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-[#FF1A1A] transition-colors">
                <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]"><path fill="currentColor" d="M12 11v2.8h4.6c-.2 1.2-1.5 3.6-4.6 3.6-2.8 0-5-2.3-5-5.1S9.2 7.2 12 7.2c1.6 0 2.6.7 3.2 1.2l2.2-2.1C16 4.9 14.2 4.1 12 4.1 7.6 4.1 4 7.7 4 12s3.6 7.9 8 7.9c4.6 0 7.7-3.2 7.7-7.8 0-.5 0-.9-.1-1.3H12z"/></svg>
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-heading text-white font-semibold mb-5">Navigatie</h4>
            <ul className="flex flex-col gap-3">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <button onClick={() => goTo(l.id)} className="text-white/55 hover:text-[#FF1A1A] text-sm transition-colors">
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-white font-semibold mb-5">Diensten</h4>
            <ul className="flex flex-col gap-3">
              {services.slice(0, 7).map((s) => (
                <li key={s.id || s.title} className="text-white/55 text-sm">{s.title}</li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-white font-semibold mb-5">Contact</h4>
            <ul className="flex flex-col gap-3 text-sm">
              <li><a href={`tel:${settings.phoneRaw}`} className="text-white/55 hover:text-white flex items-center gap-2 transition-colors"><Phone size={15} /> {settings.phone}</a></li>
              <li><a href={`mailto:${settings.email}`} className="text-white/55 hover:text-white flex items-center gap-2 transition-colors"><Mail size={15} /> {settings.email}</a></li>
              <li className="text-white/55 mt-2">{settings.location}</li>
              <li className="text-white/40 text-xs mt-2">KvK: {settings.kvk}</li>
              <li className="text-white/40 text-xs">BTW: {settings.btw}</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-14 pt-6 flex flex-col md:flex-row justify-between gap-3 text-white/40 text-xs">
          <span>© {new Date().getFullYear()} Klusbedrijf Pruijn. Alle rechten voorbehouden.</span>
          <span>{settings.website}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
